import { isBefore, subDays } from '@repo/date-time';
import { useEffect, useState } from 'react';

import { NEWS_FEED_URLS, NEWS_MAXIMUM_AGE_IN_DAYS } from '@features/parkingMap/constants/news';
import type { NewsItem } from '@features/parkingMap/types';
import { parseNewsFeed } from '@features/parkingMap/utils/parseNewsFeed';

import { useLanguageStore } from '@shared/stores/useLanguageStore';

const findLatestNews = (newsItems: NewsItem[]): NewsItem | undefined =>
  newsItems.reduce<NewsItem | undefined>(
    (latest, newsItem) =>
      latest === undefined || isBefore(latest.publishedAt, newsItem.publishedAt) ? newsItem : latest,
    undefined
  );

const isFresh = (newsItem: NewsItem): boolean =>
  !isBefore(newsItem.publishedAt, subDays(new Date(), NEWS_MAXIMUM_AGE_IN_DAYS));

// Fetches the website news feed once and returns its latest item when it is
// not older than NEWS_MAXIMUM_AGE_IN_DAYS. Not a core feature: any failure
// (network, CORS, malformed feed) silently yields undefined.
export function useLatestNews(): NewsItem | undefined {
  const language = useLanguageStore((state) => state.language);
  const [latestNews, setLatestNews] = useState<NewsItem | undefined>(undefined);

  useEffect(() => {
    const abortController = new AbortController();

    fetch(NEWS_FEED_URLS[language], { signal: abortController.signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((xml) => {
        const newsItem = findLatestNews(parseNewsFeed(xml));
        setLatestNews(newsItem !== undefined && isFresh(newsItem) ? newsItem : undefined);
      })
      .catch(() => setLatestNews(undefined));

    return () => abortController.abort();
  }, [language]);

  return latestNews;
}
